// The appointment statuses, in one place.
//
// They were in two: a filter list and a workflow dropdown, each written out by
// hand. When a migration replaced the appointments_status_enum the filter list
// happened to match the new values and the workflow dropdown did not, so the
// calendar offered three statuses the database would reject — and the rejection
// was swallowed, leaving Mongo and Postgres disagreeing about where a patient
// was.

/**
 * The values `appointments.status` accepts. A copy of the Postgres enum:
 *
 *   SELECT e.enumlabel FROM pg_enum e JOIN pg_type t ON t.oid = e.enumtypid
 *    WHERE t.typname = 'appointments_status_enum' ORDER BY e.enumsortorder;
 *
 * If that query and this list disagree, this list is wrong.
 */
export const APPOINTMENT_STATUSES = [
  'scheduled',
  'confirmed',
  'pending_arrival',
  'in_lobby',
  'in_room',
  'no_show',
  'cancelled',
  'completed',
  'tentative',
] as const;

export type AppointmentStatusValue = typeof APPOINTMENT_STATUSES[number];

export interface StatusOption {
  value: string;
  label: string;
}

/** The Status filter, which can also mean "do not filter". */
export const STATUS_FILTER_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'All Status' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending_arrival', label: 'Pending Arrival' },
  { value: 'in_lobby', label: 'In Lobby' },
  { value: 'in_room', label: 'In Room' },
  { value: 'no_show', label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Seen | Completed' },
  { value: 'tentative', label: 'Tentative' },
];

/**
 * What the front desk moves a patient through on the day, in order: booked,
 * arrived, waiting, with the doctor, done — or never came.
 *
 * Cancelled is deliberately absent. It is terminal and has its own action,
 * with a confirmation that says the slot is freed and that nothing is sent to
 * the patient; reaching it from a progression dropdown would skip all of that.
 * Tentative and confirmed are booking states rather than steps in the day.
 */
export const WORKFLOW_STATUS_OPTIONS: StatusOption[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'pending_arrival', label: 'Pending Arrival' },
  { value: 'in_lobby', label: 'In Lobby' },
  { value: 'in_room', label: 'In Room' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No Show' },
];
